import { useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import NiceModal from "@ebay/nice-modal-react";
import { MultiSelect } from "../../../components/fields/base/MultiSelect";
import { defaultTagColor } from "../../../components/core/basic/Tag";
import { useTagsQuery } from "../api/useTagsQuery";
import { TagFormModal } from "./TagFormModal";
import type { TagEntityType } from "../../../zodios/apiDefinitions/api/tags";

interface TagMultiSelectProps {
  entityType: TagEntityType;
  value: number[];
  onChange: (tagIds: number[]) => void;
  label?: ReactNode;
  ariaLabel?: string;
  isDisabled?: boolean;
}

type TagItem = { value: number; label: string; color: string };

export const TagMultiSelect = ({
  entityType,
  value,
  onChange,
  label,
  ariaLabel,
  isDisabled = false,
}: TagMultiSelectProps) => {
  const { t } = useTranslation(["tags"]);
  const tagsQuery = useTagsQuery({ entityType });
  const resolvedAriaLabel = ariaLabel ?? t(($) => $.tags.multiSelect["Select tags"]);

  const items = useMemo<TagItem[]>(
    () =>
      (tagsQuery.data?.results ?? [])
        .filter((tag) => tag.type === "user")
        .map((tag) => ({
          value: tag.id,
          label: tag.name,
          color: tag.color || defaultTagColor,
        })),
    [tagsQuery.data?.results],
  );

  const hasNoTags = !tagsQuery.isLoading && items.length === 0;
  const resolvedPlaceholder =
    tagsQuery.isLoading ? t(($) => $.tags.multiSelect["Loading tags..."])
    : hasNoTags ? t(($) => $.tags.multiSelect["No tags yet — type to create"])
    : t(($) => $.tags.multiSelect["Search tags..."]);

  const handleCreate = (searchValue: string) => {
    const trimmed = searchValue.trim();
    void NiceModal.show(TagFormModal, {
      entityType,
      initialTag: trimmed ? { name: trimmed } : undefined,
      onCreated: (tag) => onChange([...value, tag.id]),
    });
  };

  return (
    <MultiSelect<TagItem>
      aria-label={resolvedAriaLabel}
      label={label}
      items={items}
      value={value}
      onChange={(keys) => onChange(keys.map((k) => Number(k)))}
      placeholder={resolvedPlaceholder}
      isLoading={tagsQuery.isLoading}
      isDisabled={isDisabled}
      onCreate={handleCreate}
      createLabel={(s) =>
        s.trim() ?
          t(($) => $.tags.multiSelect['Create "{value}"'], { value: s.trim() })
        : t(($) => $.tags.multiSelect["Create new tag"])
      }
    />
  );
};
